import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import type AbilitiesService from 'ember-can/services/abilities';
import { isProductDisabled } from 'garaje/helpers/product-disabled';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import type UserFeatureFlagsService from 'garaje/services/user-feature-flags';
import type VisitorsOnboardingService from 'garaje/services/visitors-onboarding';
import { AnalyticsEventNames } from 'garaje/utils/enums';

import type AnalyticsVisitorsController from './controller';

class AnalyticsVisitorsIndexRoute extends Route {
  @service declare abilities: AbilitiesService;
  @service declare router: RouterService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare userFeatureFlags: UserFeatureFlagsService;
  @service declare state: StateService;
  @service declare flashMessages: FlashMessagesService;
  @service declare visitorsOnboarding: VisitorsOnboardingService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare metrics: MetricsService;

  titleToken = 'Analytics';

  setupController(controller: AnalyticsVisitorsController, model: void, transition: Transition): void {
    super.setupController(controller, model, transition);

    if (
      (this.featureFlags.isEnabled('sigma-embedded-analytics') ||
        this.userFeatureFlags.isEnabled('sigma-embedded-analytics-by-user')) &&
      !isProductDisabled('analytics-v2')
    ) {
      void controller.fetchSigmaUrlTask.perform();
    }
  }

  beforeModel(): void | Promise<unknown> {
    const { vrSubscription } = this.state;
    if (this.abilities.cannot('visit-visitors analytics')) {
      if (vrSubscription?.canAccessAnalytics) {
        this.router.transitionTo('unauthorized');
      } else {
        this.flashMessages.showFlash('error', 'Analytics is not available on this plan');
        this.router.transitionTo('dashboard');
      }
    }
  }

  async model(): Promise<void> {
    await this.visitorsOnboarding.loadSteps();
  }

  afterModel(): void {
    this.#logMetrics();
  }

  redirect(_model: unknown, transition: Transition): void {
    this.visitorsOnboarding.gateRoute(this, transition);
  }

  #logMetrics(): void {
    const {
      currentCompany: { id: companyId },
      currentLocation: { id: locationId },
    } = this.state;
    const details = {
      companyId,
      locationId,
      app: this.state.vrSubscription?.app,
      plan: this.state?.vrSubscription?.plan,
      userId: this.currentAdmin?.id || '',
      employeeId: this.currentAdmin.employee?.id || '',
    };
    this.metrics.trackEvent(AnalyticsEventNames.VISITORS_DASHBOARD_VISITED, details);
    this.metrics.logMonitorEvent(AnalyticsEventNames.VISITORS_DASHBOARD_VISITED, details);
  }
}

export default AnalyticsVisitorsIndexRoute;
